import { globalRef, type Maybe } from '@amazon/vinyl-util'
import type { ClickContext } from '@/metrics/uiMetrics'

// GH's entity context: every click inside a shelf, grid or list item carries what the item
// provides (its entity, position, blockRef, entity text), the click's own fields winning.

interface ItemClicksState {
    readonly items: WeakMap<Element, () => ClickContext>
    readonly openers: WeakMap<Element, Element>
}

const itemClicksRef = globalRef<ItemClicksState>(() => ({
    items: new WeakMap(),
    openers: new WeakMap(),
}))

/** Makes `item` a list item whose clicks carry `clicks`, read as each click is reported. */
export function provideItemClicks<T extends Element>(item: T, clicks: () => ClickContext): T {
    itemClicksRef.value.items.set(item, clicks)
    return item
}

/** A menu opened from `opener` belongs to the item `opener` is in, wherever it is mounted. */
export function openedFrom(menu: Element, opener: Maybe<Element>): void {
    if (opener) itemClicksRef.value.openers.set(menu, opener)
}

/** What the nearest item around `element` (through the menus it opened) adds to a click. */
export function itemClicksAt(element: Maybe<Element>): Maybe<ClickContext> {
    const { items, openers } = itemClicksRef.value
    for (let el = element; el; el = openers.get(el) ?? el.parentElement) {
        const clicks = items.get(el)
        if (clicks) return clicks()
    }
    return null
}
